import { useCallback, useEffect, useState } from 'react';

import { mensagemErro } from '../api/axios';
import { definirPolitica, listarOperadoras } from '../api/fluxfin';
import { Alerta, Badge, Botao, LoadingSpinner, Painel, Tabela, Th } from './common';
import { useOrdenacao } from '../hooks/useOrdenacao';

const CONTAS = [
  { valor: 'recebido', rotulo: 'Recebido' },
  { valor: 'glosado', rotulo: 'Glosado' },
  { valor: 'retido', rotulo: 'Retenção judicial' },
];

/* Três estados, não dois. `null` é "ainda não se sabe", e é o padrão — a
   diferença entre não saber e saber que não leva a condutas opostas, e já
   custou a esta base um cartão verde onde faltava arquivo. */
const TRES = [
  { valor: '', rotulo: 'Não verificado' },
  { valor: 'sim', rotulo: 'Sim' },
  { valor: 'nao', rotulo: 'Não' },
];

const paraBool = (v) => (v === '' ? null : v === 'sim');
const paraTexto = (b) => (b === null || b === undefined ? '' : b ? 'sim' : 'nao');

/**
 * Como cada operadora fecha o ciclo glosa → recurso → pagamento.
 *
 * Não é cadastro decorativo: a primeira resposta decide se a detecção de
 * recuperação de glosa sequer se aplica àquela operadora, e a terceira decide
 * quais contas o título aceita.
 */
export default function PoliticaOperadoras() {
  const [operadoras, setOperadoras] = useState(null);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const ord = useOrdenacao(operadoras);

  const carregar = useCallback(() => {
    listarOperadoras()
      .then((r) => setOperadoras(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar.')));
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrir = (o) =>
    setEditando({
      id: o.id_operadora,
      nome: o.nome,
      recurso: paraTexto(o.recurso_gera_novo_protocolo),
      identificavel: paraTexto(o.recuperacao_identificavel_no_arquivo),
      contas: o.contas_aplicaveis || CONTAS.map((c) => c.valor),
      observacao: o.observacao_politica || '',
    });

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      await definirPolitica(editando.id, {
        recurso_gera_novo_protocolo: paraBool(editando.recurso),
        recuperacao_identificavel_no_arquivo: paraBool(editando.identificavel),
        contas_aplicaveis: editando.contas,
        observacao_politica: editando.observacao.trim() || null,
      });
      setAviso(`Política de ${editando.nome} registrada.`);
      setEditando(null);
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  const alternarConta = (conta) => {
    const tem = editando.contas.includes(conta);
    setEditando({
      ...editando,
      contas: tem
        ? editando.contas.filter((c) => c !== conta)
        : [...editando.contas, conta],
    });
  };

  if (!operadoras) return <LoadingSpinner />;

  return (
    <>
      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="ok" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      <Painel
        titulo="Política de ciclo por operadora"
        subtitulo="Como cada uma fecha o caminho glosa → recurso → pagamento"
      >
        <p style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
          <strong>&quot;Não verificado&quot; é uma resposta legítima</strong> —
          e é o padrão. Enquanto a primeira pergunta estiver sem resposta, o
          sistema continua propondo recuperações de glosa, mas avisa que a
          política não foi conferida. Se a resposta for <em>sim</em>, a
          detecção deixa de se aplicar àquela operadora: o dinheiro do recurso
          aceito entra em outro título, e procurá-lo dentro do protocolo
          original é procurar no lugar errado.
        </p>

        <Tabela>
          <thead>
            <tr>
              <Th campo="nome" estado={ord}>Operadora</Th>
              <Th campo="recurso_gera_novo_protocolo" estado={ord}>Recurso gera protocolo novo?</Th>
              <Th campo="recuperacao_identificavel_no_arquivo" estado={ord}>A recuperação vem marcada no arquivo?</Th>
              <Th campo="contas_aplicaveis" estado={ord}>Contas do título</Th>
              <Th alinhar="right">Ação</Th>
            </tr>
          </thead>
          <tbody>
            {ord.ordenados.map((o) => (
              <tr key={o.id_operadora}>
                <td style={{ fontWeight: 600 }}>{o.nome}</td>
                <td>
                  <Resposta valor={o.recurso_gera_novo_protocolo} />
                </td>
                <td>
                  <Resposta valor={o.recuperacao_identificavel_no_arquivo} />
                </td>
                <td style={{ fontSize: 13 }}>
                  {(o.contas_aplicaveis || CONTAS.map((c) => c.valor))
                    .map((c) => CONTAS.find((x) => x.valor === c)?.rotulo || c)
                    .join(' · ')}
                  {o.observacao_politica && (
                    <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                      {o.observacao_politica}
                    </div>
                  )}
                </td>
                <td>
                  <Botao variante="secundario" pequeno onClick={() => abrir(o)}>
                    Editar
                  </Botao>
                </td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      </Painel>

      {editando && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ background: 'rgba(16,24,40,.5)' }}
          />
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div
                className="modal-content"
                style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
              >
                <div
                  className="modal-header"
                  style={{ borderColor: 'var(--ff-border-soft)' }}
                >
                  <h2 className="ff-painel__titulo">{editando.nome}</h2>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setEditando(null)}
                    aria-label="Fechar"
                  />
                </div>
                <div className="modal-body d-flex flex-column gap-4">
                  <Pergunta
                    titulo="Quando um recurso de glosa é aceito, a operadora gera um protocolo novo?"
                    ajuda="Se sim, o dinheiro entra em outro título e a detecção de recuperação não se aplica aqui. Na Unimed a resposta é não: ela paga dentro do mesmo protocolo."
                    valor={editando.recurso}
                    aoMudar={(v) => setEditando({ ...editando, recurso: v })}
                  />
                  <Pergunta
                    titulo="O arquivo diz que aquele pagamento é a volta de um recurso?"
                    ajuda="Nenhuma das operadoras conhecidas marca isso. É por essa ausência que a recuperação depende de confirmação humana."
                    valor={editando.identificavel}
                    aoMudar={(v) => setEditando({ ...editando, identificavel: v })}
                  />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      Quais contas existem no título desta operadora?
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                      Retenção judicial não é universal. Desmarcá-la impede o
                      lançamento manual nessa conta — oferecer uma baixa que não
                      existe convida ao número inventado.
                    </p>
                    <div className="d-flex gap-3 flex-wrap">
                      {CONTAS.map((c) => (
                        <label
                          key={c.valor}
                          className="d-flex align-items-center gap-2"
                          style={{ fontSize: 14 }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={editando.contas.includes(c.valor)}
                            onChange={() => alternarConta(c.valor)}
                          />
                          {c.rotulo}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                      Observação (o que se sabe, e como se soube)
                    </label>
                    <textarea
                      className="ff-input"
                      rows={2}
                      value={editando.observacao}
                      onChange={(e) =>
                        setEditando({ ...editando, observacao: e.target.value })
                      }
                      placeholder="Confirmado com o faturamento em …"
                    />
                  </div>
                </div>
                <div
                  className="modal-footer"
                  style={{ borderColor: 'var(--ff-border-soft)' }}
                >
                  <Botao variante="secundario" onClick={() => setEditando(null)}>
                    Cancelar
                  </Botao>
                  <Botao
                    onClick={salvar}
                    disabled={salvando || editando.contas.length === 0}
                  >
                    Salvar política
                  </Botao>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Resposta({ valor }) {
  if (valor === null || valor === undefined)
    return <Badge tom="arquivado">não verificado</Badge>;
  return <Badge tom={valor ? 'pendente' : 'confirmado'}>{valor ? 'sim' : 'não'}</Badge>;
}

function Pergunta({ titulo, ajuda, valor, aoMudar }) {
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 14 }}>{titulo}</div>
      <p style={{ fontSize: 13, color: 'var(--ff-muted)' }}>{ajuda}</p>
      <div className="d-flex gap-3">
        {TRES.map((o) => (
          <label
            key={o.valor}
            className="d-flex align-items-center gap-2"
            style={{ fontSize: 14 }}
          >
            <input
              type="radio"
              className="form-check-input"
              checked={valor === o.valor}
              onChange={() => aoMudar(o.valor)}
            />
            {o.rotulo}
          </label>
        ))}
      </div>
    </div>
  );
}
